// Actions for authentication

import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  OnChangePasswordRequest,
  OnLoginRequest,
  OnLoginResponse,
  OnRegisterRequest,
  OnRegisterResponse,
} from "../interfaces/auth.types.ts";
import { UserID } from "../interfaces/user.types.ts";
import Cookies from "js-cookie";
import api from "../../api";
import { generateAuthCookie } from "../../utils/auth/generateAuthCookie.ts";
import { getExceptionMessage } from "../../exceptions/helpers/getExceptionMessage.ts";
import { authExceptions } from "../../exceptions/default/authExceptions.ts";
import { clearAllVaultDrafts } from "../../utils/vault/vaultFormStorage.ts";
import { queryClient } from "../../lib/queryClient.ts";
import { vaultKeys } from "../../hooks/vault/queryKeys.ts";
import {
  clearStoredStaffActorIdentity,
  clearStoredVaultActorSession,
  setStoredStaffActorIdentity,
} from "../../utils/vault/vaultActorSession.ts";

export const onLogin = createAsyncThunk(
  "auth/onLogin",
  async (loginData: OnLoginRequest, thunkAPI) => {
    try {
      const response = await api.post<OnLoginResponse>(
        "/accounts/user/login",
        loginData,
      );
      generateAuthCookie(response.data.token);
      return response.data;
    } catch (error: any) {
      const msg = getExceptionMessage(
        error.response.data?.code,
        authExceptions.onLogin,
      );
      return thunkAPI.rejectWithValue(msg);
    }
  },
);

export const onRegister = createAsyncThunk(
  "auth/onRegister",
  async (registerData: OnRegisterRequest, thunkAPI) => {
    try {
      const response = await api.post<OnRegisterResponse>(
        "/accounts/user/register",
        registerData,
      );
      return response.data;
    } catch (error: any) {
      const msg = getExceptionMessage(
        error.response.data?.code,
        authExceptions.onRegister,
      );
      return thunkAPI.rejectWithValue(msg);
    }
  },
);

export const onGetUserInfo = createAsyncThunk(
  "auth/onUserGetInfo",
  async (_, thunkAPI) => {
    try {
      const response = await api.get<UserID>("/accounts/user");
      setStoredStaffActorIdentity(response.data.id);
      return response.data;
    } catch (error: any) {
      const msg = getExceptionMessage(
        error.response.data?.code,
        authExceptions.onGetUserInfo,
      );
      return thunkAPI.rejectWithValue(msg);
    }
  },
);

export const onChangePassword = createAsyncThunk(
  "auth/onChangePassword",
  async (changePasswordData: OnChangePasswordRequest, thunkAPI) => {
    try {
      await api.post("/accounts/user/change-password", changePasswordData);
    } catch (error: any) {
      const msg = getExceptionMessage(
        error.response.data?.code,
        authExceptions.onChangePassword,
      );
      return thunkAPI.rejectWithValue(msg);
    }
  },
);

export const onLogout = createAsyncThunk("auth/onLogout", async (_) => {
  Cookies.remove("session");
  clearStoredVaultActorSession();
  clearStoredStaffActorIdentity();
  sessionStorage.removeItem("folderFilter");
  sessionStorage.removeItem("folderSort");
  clearAllVaultDrafts();
  queryClient.removeQueries({ queryKey: vaultKeys.all });
});
